<template>
  <div
    :class="
      twMerge(
        'relative flex items-center border border-gray-200 rounded-lg shadow-lg shadow-gray-100 overflow-hidden',
        sizeClasses.root,
        $attrs.class as string
      )
    "
  >
    <button
      id="decrement-button"
      type="button"
      data-input-counter-decrement="quantity-input"
      :class="['h-full outline-0 bg-white', sizeClasses.button]"
      :disabled="disabled || value === 0 || disableMinus"
      @click="handleDecrease"
    >
      <Minus
        :class="[
          sizeClasses.icon,
          'text-gray-900',
          { 'text-gray-400!': disabled || value === 0 || disableMinus },
        ]"
      />
    </button>
    <input
      id="quantity-input"
      v-model="internalValue"
      type="text"
      data-input-counter
      aria-describedby="helper-text-explanation"
      :class="[
        'disabled:bg-white disabled:text-gray-400 border-gray-300 text-center text-gray-900 focus:ring-blue-500 focus:border-blue-500 block w-full',
        sizeClasses.input,
      ]"
      placeholder="999"
      :disabled="disabled"
      required
      :readonly="isReadonly"
      @change="
        handleChangeInputValue(($event.target as HTMLInputElement)?.value ?? '')
      "
    />

    <button
      id="increment-button"
      type="button"
      data-input-counter-increment="quantity-input"
      :class="['h-full outline-hidden bg-white', sizeClasses.button]"
      :disabled="disabled || disablePlus"
      @click="handleIncrease"
    >
      <Plus
        :class="[
          sizeClasses.icon,
          'text-gray-900',
          { 'text-gray-400!': disabled || disablePlus },
        ]"
      />
    </button>
  </div>
</template>

<script lang="ts" setup>
import { twMerge } from 'tailwind-merge'
import Plus from '~/assets/icons/plus.svg?component'
import Minus from '~/assets/icons/minus.svg?component'

const props = defineProps<{
  max?: number
  min?: number
  disableMinus?: boolean
  disablePlus?: boolean
  disabled?: boolean
  isReadonly?: boolean
  isDatesLoaded?: boolean
  size?: 'sm' | 'lg'
}>()

const sizeClasses = computed(() => {
  if (props.size === 'sm') {
    return {
      root: 'max-w-24 h-7',
      button: 'px-2',
      icon: 'w-2.5 h-2.5',
      input: 'text-xs py-1',
    }
  }

  return {
    root: 'max-w-32 h-8',
    button: 'px-3',
    icon: 'w-3 h-3',
    input: 'text-sm py-2.5',
  }
})

const value = defineModel<number>({
  default: 0,
})

const emit = defineEmits(['maxReached', 'minReached', 'change'])

const internalValue = ref(value.value)

const handleIncrease = () => {
  handleChangeInputValue(Number(internalValue.value) + 1)
  emit('change', value.value)
}

const handleDecrease = () => {
  handleChangeInputValue(Number(internalValue.value) - 1)
  emit('change', value.value)
}

const handleChangeInputValue = (newVal: string | number) => {
  // Convert input to number and handle invalid input
  const numericValue = Number(newVal)
  if (isNaN(numericValue)) {
    internalValue.value = props.min ?? 0
    value.value = internalValue.value
    emit('change', value.value)
    return
  }

  // Apply min/max constraints
  let finalValue = numericValue
  if (props.max && finalValue > props.max) {
    finalValue = props.max
    emit('maxReached')
  }
  if (props.min && finalValue < props.min) {
    finalValue = props.min
    emit('minReached')
  }

  // Update both internal and model values
  internalValue.value = finalValue
  value.value = finalValue
  emit('change', finalValue)
}

watch(
  () => props.min,
  () => {
    handleChangeInputValue(internalValue.value)
  }
)

watch(
  () => props.max,
  () => {
    handleChangeInputValue(internalValue.value)
  }
)

watch(
  () => value.value,
  () => {
    internalValue.value = value.value
  }
)
</script>
